import React from 'react';
import PageWrapper from '@/components/ui/PageWrapper';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';

const allServices = [
  {
    id: 'exterior',
    title: 'Exterior Detailing',
    desc: 'Comprehensive safe wash, decontamination, and sealant application to restore gloss and protect your paint from the harsh Montreal elements.',
    benefits: ['Safe hand wash', 'Iron & tar removal', 'Clay bar treatment', 'Silica sealant (3-6 months)'],
    duration: '2-4 Hours',
    icon: FiIcons.FiWind
  },
  {
    id: 'interior',
    title: 'Interior Detailing',
    desc: 'Deep cleaning of all interior surfaces. We extract carpets, condition leather, and sanitize the cabin to bring back that new car feel and smell.',
    benefits: ['Hot water extraction', 'Leather deep clean & condition', 'Plastics UV protection', 'Odor neutralization'],
    duration: '3-5 Hours',
    icon: FiIcons.FiLayers
  },
  {
    id: 'correction',
    title: 'Paint Correction',
    desc: 'Machine polishing to permanently remove swirl marks, scratches, water spots, and oxidation, revealing a flawless, mirror-like finish.',
    benefits: ['Removes 80-95% of defects', 'Restores deep gloss', 'Prepares paint for coating', 'Increases vehicle value'],
    duration: '1-3 Days',
    icon: FiIcons.FiSun
  },
  {
    id: 'ceramic',
    title: 'Ceramic Coating',
    desc: 'The ultimate protection. A liquid polymer that chemically bonds with your paint, creating a hard, hydrophobic layer that lasts for years.',
    benefits: ['Extreme gloss', 'Self-cleaning properties', 'UV & chemical resistance', 'Up to 5 years durability'],
    duration: '2-4 Days',
    icon: FiIcons.FiShield
  },
  {
    id: 'engine',
    title: 'Engine Bay Cleaning',
    desc: 'Careful degreasing and cleaning of the engine compartment, followed by dressing of all plastics and hoses for a show-room look.',
    benefits: ['Removes grease & grime', 'Protects hoses from cracking', 'Easier to spot leaks', 'Show-ready appearance'],
    duration: '1 Hour',
    icon: FiIcons.FiSettings
  },
  {
    id: 'headlight',
    title: 'Headlight Restoration',
    desc: 'Sanding and polishing of oxidized, yellowed headlights, finished with a UV-resistant ceramic coating to prevent future fading.',
    benefits: ['Improves night visibility', 'Enhances aesthetics', 'Passes safety inspections', 'Permanent fix (coated)'],
    duration: '1.5 Hours',
    icon: FiIcons.FiEye
  }
];

const Services = () => {
  return (
    <PageWrapper>
      <div className="bg-primary py-24 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-10">
          <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6">Our Services</h1>
          <p className="text-xl text-textMuted max-w-3xl mx-auto">
            From meticulous hand washes to multi-stage paint correction and ceramic coatings, we offer a comprehensive suite of services to transform and protect your vehicle.
          </p>
        </div>
      </div>

      <div className="py-24 bg-bgDark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {allServices.map((service, idx) => (
              <motion.div 
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-secondary/30 border border-white/5 rounded-2xl p-8 hover:border-accent/40 transition-colors flex flex-col h-full"
              >
                <div className="bg-primary w-14 h-14 rounded-xl flex items-center justify-center mb-6 border border-white/10">
                  <SafeIcon icon={service.icon} className="text-accent text-2xl" />
                </div>
                <h3 className="text-2xl font-bold font-heading mb-4">{service.title}</h3>
                <p className="text-textMuted mb-6 flex-grow">{service.desc}</p>
                
                <div className="space-y-3 mb-8">
                  {service.benefits.map((benefit, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <SafeIcon icon={FiIcons.FiCheck} className="text-accent mt-1 flex-shrink-0" />
                      <span className="text-sm text-textLight/80">{benefit}</span>
                    </div>
                  ))}
                </div>
                
                <div className="pt-6 border-t border-white/10 mt-auto flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-textMuted">
                    <SafeIcon icon={FiIcons.FiClock} />
                    <span>{service.duration}</span>
                  </div>
                  <Link to={`/booking?service=${service.id}`} className="text-accent hover:text-white text-sm font-medium transition-colors">
                    Book Now →
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default Services;